import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import {
    type AgentSkill,
    getAgentSkills,
    skillsNamedIn
} from '@/services/agent_skills';

/**
 * The skills this member can name with `/<name>`. Shared by the autocomplete and the send, so the
 * two cannot disagree. Switched-on only: the run does not mount the others.
 */
export const useAgentSkillsStore = defineStore('agentSkills', () => {
    const skills = ref<AgentSkill[]>([]);
    const loadedTenantId = ref<string | null>(null);

    const activeSkills = computed(() =>
        skills.value.filter((skill) => !skill.excluded)
    );

    async function load(tenantId: string, force = false) {
        if (!tenantId) return;
        if (!force && loadedTenantId.value === tenantId) return;
        try {
            skills.value = await getAgentSkills(tenantId);
            loadedTenantId.value = tenantId;
        } catch (error) {
            // A lost convenience, not a broken chat: skills still trigger on their description.
            console.error('Could not load skills for the composer', error);
            skills.value = [];
            // Cleared too, or the next call short-circuits on a tenant whose list we just wiped.
            loadedTenantId.value = null;
        }
    }

    /** The skills a message names with `/<name>`. Here so the four send paths cannot drift. */
    function namedIn(message: string | undefined): string[] {
        if (!message) return [];
        return skillsNamedIn(
            message,
            activeSkills.value.map((skill) => skill.name)
        );
    }

    return { skills, activeSkills, load, namedIn };
});
